import { useEffect, useMemo, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: "", permisos: [] });
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const [r, d] = await Promise.all([api.get("/roles"), api.get("/permisos/definiciones")]);
      setRoles(r.data); setPermisos(d.data.permisos);
    } catch (e) { toast.error(errorMsg(e)); }
  };
  useEffect(() => { cargar(); }, []);

  const grupos = useMemo(() => {
    const m = {};
    permisos.forEach((p) => { (m[p.grupo] = m[p.grupo] || []).push(p); });
    return m;
  }, [permisos]);

  const prefijosConVer = useMemo(() => {
    const todasClaves = permisos.map((p) => p.clave);
    const set = new Set();
    permisos.forEach((p) => {
      const base = p.clave.split(".")[0];
      if (todasClaves.includes(`${base}.ver`)) set.add(base);
    });
    return set;
  }, [permisos]);

  const abrir = (rol) => {
    setEditando(rol || null);
    setForm(rol ? { nombre: rol.nombre, permisos: rol.permisos || [] } : { nombre: "", permisos: [] });
    setOpen(true);
  };

  const toggle = (clave) => setForm((f) => {
    const tiene = f.permisos.includes(clave);
    const base = clave.split(".")[0];
    let nuevos;
    if (tiene) {
      nuevos = f.permisos.filter((c) => c !== clave);
      if (prefijosConVer.has(base) && clave === `${base}.ver`) {
        nuevos = nuevos.filter((c) => c.split(".")[0] !== base);
      }
    } else {
      nuevos = [...f.permisos, clave];
      if (prefijosConVer.has(base) && clave !== `${base}.ver`) {
        nuevos = [...new Set([...nuevos, `${base}.ver`])];
      }
    }
    return { ...f, permisos: nuevos };
  });

  const marcarGrupo = (grupo, marcar) => setForm((f) => {
    const clavesGrupo = (grupos[grupo] || []).map((p) => p.clave);
    const nuevos = marcar
      ? [...new Set([...f.permisos, ...clavesGrupo])]
      : f.permisos.filter((c) => !clavesGrupo.includes(c));
    return { ...f, permisos: nuevos };
  });

  const guardar = async () => {
    setGuardando(true);
    try {
      if (editando) await api.put(`/roles/${editando.id}`, { nombre: form.nombre, permisos: form.permisos });
      else await api.post("/roles", { nombre: form.nombre, permisos: form.permisos });
      toast.success(editando ? "Rol actualizado" : "Rol creado");
      setOpen(false); cargar();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  const eliminar = async (rol) => {
    if (!window.confirm(`¿Eliminar el rol "${rol.nombre}"?`)) return;
    try { await api.delete(`/roles/${rol.id}`); toast.success("Rol eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div data-testid="roles-page" className="space-y-4">
      <Panel titulo="Roles y permisos" testId="roles-panel">
        <p className="mb-3 text-xs text-slate-500">
          Todo lo no concedido está denegado. El rol <b>Administrador</b> siempre tiene acceso total
          (incluidos los permisos nuevos); los demás roles solo reciben lo nuevo cuando un
          administrador se lo concede.
        </p>
        <Button data-testid="rol-crear-btn" onClick={() => abrir(null)}
          className="mb-4 bg-indigo-600 hover:bg-indigo-700 text-white">
          <Plus size={15} className="mr-1.5" /> Crear rol
        </Button>
        <div className="space-y-2">
          {roles.map((r, i) => (
            <div key={r.id} data-testid={`rol-fila-${i}`}
              className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
              <ShieldCheck size={18} className={r.es_admin ? "text-indigo-600" : "text-slate-400"} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800">{r.nombre}
                  {r.es_admin && <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-700">acceso total</span>}
                </p>
                {!r.es_admin && (
                  <p className="text-xs text-slate-500">{(r.permisos || []).length} permiso(s)</p>
                )}
              </div>
              {!r.es_admin && (
                <>
                  <button data-testid={`rol-editar-${i}`} title="Editar" onClick={() => abrir(r)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                    <Pencil size={13} />
                  </button>
                  <button data-testid={`rol-eliminar-${i}`} title="Eliminar" onClick={() => eliminar(r)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rol-dialog">
          <DialogHeader><DialogTitle>{editando ? "Editar rol" : "Crear rol"}</DialogTitle>
            <DialogDescription>Marque los permisos que este rol puede usar. Lo no marcado queda denegado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Nombre del rol *</label>
              <Input data-testid="rol-nombre-input" value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Mecánico" />
            </div>
            {Object.entries(grupos).map(([grupo, items]) => {
              const marcados = items.filter((p) => form.permisos.includes(p.clave)).length;
              return (
                <div key={grupo}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase text-slate-500">{grupo}</p>
                    <button type="button" data-testid={`rol-grupo-todo-${grupo}`}
                      onClick={() => marcarGrupo(grupo, marcados < items.length)}
                      className="text-[11px] font-medium text-indigo-600 underline hover:text-indigo-800">
                      {marcados < items.length ? "Marcar todo" : "Desmarcar todo"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {items.map((p) => (
                      <label key={p.clave} className="flex items-center gap-2 text-sm text-slate-700">
                        <Checkbox data-testid={`rol-permiso-${p.clave}`}
                          checked={form.permisos.includes(p.clave)}
                          onCheckedChange={() => toggle(p.clave)} />
                        {p.nombre}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="rol-guardar-btn" onClick={guardar}
                disabled={guardando || !form.nombre.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {guardando ? "Guardando..." : "Guardar rol"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
